/**
 * Lecture minimale d'une archive ZIP (exports 1Password `.1pux`, Dashlane
 * `.zip`). Purement locale, aucune dependance : la decompression DEFLATE
 * utilise l'API native `DecompressionStream('deflate-raw')` du navigateur.
 *
 * Perimetre volontairement restreint : methodes 0 (stocke) et 8 (deflate),
 * pas de ZIP64, pas de chiffrement ZIP. Tailles decompressees plafonnees
 * (protection contre une « bombe ZIP » choisie par l'utilisateur).
 */

/** Plafond par fichier decompresse : un export de coffre depasse rarement quelques Mo. */
export const MAX_ZIP_ENTRY_BYTES = 256 * 1024 * 1024;

const EOCD_SIGNATURE = 0x06054b50;
const CENTRAL_SIGNATURE = 0x02014b50;
const LOCAL_SIGNATURE = 0x04034b50;

export class InvalidZipError extends Error {
  constructor(message: string) {
    super(`archive ZIP invalide : ${message}`);
    this.name = 'InvalidZipError';
  }
}

/**
 * Decompresse un flux DEFLATE brut (ZIP), zlib (fichier joint SafeInCloud) ou
 * GZIP (charge utile KDBX 4) avec
 * l'API native `DecompressionStream`, taille de sortie plafonnee.
 *
 * @throws {InvalidZipError} donnees corrompues ou sortie au-dela de `maxBytes`
 */
export async function decompress(
  data: Uint8Array,
  format: 'deflate-raw' | 'deflate' | 'gzip',
  maxBytes: number = MAX_ZIP_ENTRY_BYTES,
): Promise<Uint8Array> {
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new DecompressionStream(format));
  const reader = stream.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      total += value.byteLength;
      if (total > maxBytes) throw new InvalidZipError('fichier decompresse trop volumineux');
      chunks.push(value);
    }
  } catch (cause) {
    await reader.cancel().catch(() => undefined);
    if (cause instanceof InvalidZipError) throw cause;
    throw new InvalidZipError('donnees compressees corrompues');
  }
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return out;
}

/**
 * Extrait les fichiers d'une archive ZIP (dossiers ignores).
 *
 * @throws {InvalidZipError} structure illisible, ZIP64, methode non supportee,
 *   fichier chiffre ou decompression au-dela de `maxEntryBytes`
 */
export async function readZipEntries(
  bytes: Uint8Array,
  maxEntryBytes: number = MAX_ZIP_ENTRY_BYTES,
): Promise<Map<string, Uint8Array>> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  // L'enregistrement de fin fait 22 octets + un commentaire de 65 535 octets au plus.
  let eocd = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 22 - 0xffff); i--) {
    if (view.getUint32(i, true) === EOCD_SIGNATURE) {
      eocd = i;
      break;
    }
  }
  if (eocd === -1) throw new InvalidZipError('fin de repertoire introuvable');
  const entryCount = view.getUint16(eocd + 10, true);
  let cursor = view.getUint32(eocd + 16, true);
  if (entryCount === 0xffff || cursor === 0xffffffff) throw new InvalidZipError('ZIP64 non supporte');

  const decoder = new TextDecoder();
  const entries = new Map<string, Uint8Array>();
  try {
    for (let n = 0; n < entryCount; n++) {
      if (view.getUint32(cursor, true) !== CENTRAL_SIGNATURE) throw new InvalidZipError('repertoire central corrompu');
      const flags = view.getUint16(cursor + 8, true);
      const method = view.getUint16(cursor + 10, true);
      const compressedSize = view.getUint32(cursor + 20, true);
      const nameLength = view.getUint16(cursor + 28, true);
      const extraLength = view.getUint16(cursor + 30, true);
      const commentLength = view.getUint16(cursor + 32, true);
      const localOffset = view.getUint32(cursor + 42, true);
      const name = decoder.decode(bytes.subarray(cursor + 46, cursor + 46 + nameLength));
      cursor += 46 + nameLength + extraLength + commentLength;
      if (name.endsWith('/')) continue;
      if ((flags & 0x1) !== 0) throw new InvalidZipError('archive chiffree non supportee');
      if (compressedSize === 0xffffffff || localOffset === 0xffffffff) throw new InvalidZipError('ZIP64 non supporte');
      if (view.getUint32(localOffset, true) !== LOCAL_SIGNATURE) throw new InvalidZipError('en-tete local corrompu');
      const dataStart = localOffset + 30 + view.getUint16(localOffset + 26, true) + view.getUint16(localOffset + 28, true);
      if (dataStart + compressedSize > bytes.length) throw new InvalidZipError('fichier tronque');
      const data = bytes.subarray(dataStart, dataStart + compressedSize);
      if (method === 0) {
        if (data.length > maxEntryBytes) throw new InvalidZipError('fichier decompresse trop volumineux');
        entries.set(name, data);
      } else if (method === 8) {
        entries.set(name, await decompress(data, 'deflate-raw', maxEntryBytes));
      } else {
        throw new InvalidZipError(`methode de compression ${method} non supportee`);
      }
    }
  } catch (cause) {
    if (cause instanceof InvalidZipError) throw cause;
    // RangeError du DataView : offsets hors limites = archive tronquee/forgee.
    throw new InvalidZipError('structure hors limites');
  }
  return entries;
}
